import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/SimpleToast";
import { mailingApi } from "@/lib/api";
import { Sparkles, Loader2, Check, Wand2 } from "lucide-react";

const TONES = [
  { value: "freundlich", label: "Freundlich" },
  { value: "formell", label: "Formell" },
  { value: "ueberzeugend", label: "Überzeugend (Sales)" },
  { value: "kurz", label: "Kurz & knapp" },
];

export function AiTextDialog({ open, onOpenChange, onInsert }) {
  const toast = useToast();
  const [instruction, setInstruction] = useState("");
  const [tone, setTone] = useState("freundlich");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setInstruction(""); setResult(""); setTone("freundlich"); };

  const generate = async () => {
    if (!instruction.trim()) { toast.error("Bitte eine kurze Anweisung eingeben"); return; }
    setLoading(true);
    try {
      const res = await mailingApi.aiText({ action: "draft", instruction: instruction.trim(), tone });
      setResult(res.data.text || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Textgenerierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const insert = () => {
    if (!result.trim()) return;
    onInsert(result.trim());
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="ai-text-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> KI-Textassistent</DialogTitle>
          <DialogDescription>Beschreibe, worum es in der E-Mail gehen soll – die KI erstellt einen Entwurf mit Platzhaltern.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="ai-instruction">Worum geht es?</Label>
            <Textarea id="ai-instruction" rows={3} value={instruction} onChange={(e) => setInstruction(e.target.value)}
              placeholder="z. B. Kunden über ein geplantes Firewall-Update am Freitag informieren" data-testid="ai-text-instruction" />
          </div>
          <div className="space-y-2">
            <Label>Tonfall</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="ai-text-tone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" onClick={generate} disabled={loading} className="w-full" data-testid="ai-text-generate">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {result ? "Neu generieren" : "Entwurf erstellen"}
          </Button>
          {result && (
            <div className="space-y-2">
              <Label htmlFor="ai-result">Entwurf (bearbeitbar)</Label>
              <Textarea id="ai-result" rows={9} value={result} onChange={(e) => setResult(e.target.value)} data-testid="ai-text-result" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { onOpenChange(false); reset(); }}>Abbrechen</Button>
          <Button type="button" onClick={insert} disabled={!result.trim()} data-testid="ai-text-insert">
            <Check className="w-4 h-4 mr-2" /> Als Textblock übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
